'use client';
import { memo } from 'react';
import { MSProductWithFullDetailWithVaraintPreset } from '@/util/query/product-query';
import dynamic from 'next/dynamic';
import { CUSTOMER_LEVEL } from '@prisma/client';
import CollectionListItemClient from '@/components/collections/collection-list-favorite-client';
import ProductTagRenderer from '@/components/product-tag-renderer';
import ProductTag from '@/components/product-tag';
import { useContext } from 'react';
import ColorStyleContext from '@/lib/providers/color-style-privider';
import { borderRadiusShorthand, useLayoutStyle } from '@/lib/providers/layout-style-provider';
import { useTranslation } from '@/app/i18n/client';
import SmartImage from '@/components/SmartImage';

const TAG_TEXT_MAX_LENGTH = 14;

const truncateTagText = (value: string, maxLength: number) => {
	if (!value || value.length <= maxLength) return value;
	return `${value.slice(0, Math.max(0, maxLength - 1))}…`;
};

interface ProductCardBorderGradientShortDescProps {
	product: MSProductWithFullDetailWithVaraintPreset;
	href: string;
	level: CUSTOMER_LEVEL;
	refreshfav?: boolean;
	variantId?: string;
	priorityImage?: boolean;
	shopCurrency: string;
	favoriteEnable: boolean;
}

const ProductCardBorderGradientShortDescInner = ({
	product,
	href,
	level,
	refreshfav,
	variantId,
	priorityImage,
	shopCurrency,
	favoriteEnable,
}: ProductCardBorderGradientShortDescProps) => {
	const { t } = useTranslation();
	const colorStyle = useContext(ColorStyleContext);
	const layoutStyle = useLayoutStyle();

	const title = product.attributes?.displayName ?? '';
	const shortDescription = product.attributes?.shortDescription ?? '';

	const variant = variantId
		? product.productVariant.find((v) => v.id === variantId) ?? product.productVariant[0] ?? null
		: product.productVariant[0] ?? null;

	const visibleTags = (product.tags || []).filter((tag: any) => tag.show);
	const primaryTag = visibleTags[0] || null;
	const overflowTagCount = Math.max(visibleTags.length - 1, 0);
	const primaryTagForThisCard = primaryTag
		? (() => {
			const tag = primaryTag as any;
			return {
				...tag,
				attributes: {
					...tag.attributes,
					tagName: truncateTagText(tag.attributes?.tagName ?? '', TAG_TEXT_MAX_LENGTH),
				},
			};
		})()
		: null;

	let imageUrl = product.featuredImageUrl
		? product.featuredImageUrl
		: (variant ? variant.featuredImageUrl : '') || '';
	const imageIndex = product.imagesUrl.findIndex((img) => img === imageUrl);
	const imageAlt = product.imagesAlt[imageIndex] || `product-${product.id}`;

	// const primaryColor = colorStyle?.buttonBgNormal || '#F58620';
	// const buttonTextColor = colorStyle?.buttonTextNormal || '#ffffff';
	// const buttonHoverBg = colorStyle?.buttonBgHover || '#d4731a';
    const primaryColor = '#F58620';
	const buttonTextColor = '#ffffff';
	const buttonHoverBg = '#d4731a';

	return (
		<>
		<div
			id={`product-card-border-gradient-short-desc-${product.id}`}
			className="flex flex-col w-full h-full relative"
			style={{
				borderRadius: '10px',
				border: '1px solid transparent',
				background: `linear-gradient(white, white) padding-box padding-box, linear-gradient(to top,rgb(255, 153, 90) 0%, rgb(226, 226, 226) 100%) border-box border-box`,
				paddingTop: '8px',
				paddingLeft: '20px',
				paddingRight: '20px',
				paddingBottom: '20px',
			}}
		>
			{/* Tags overlaid top-left of image */}
			{primaryTagForThisCard && (
				<div
					id="product-card-bgsd-tags"
					className="absolute top-2 left-2 right-2 z-50 flex items-start gap-1 overflow-hidden pointer-events-none lg:hidden"
				>
					<div className="min-w-0 max-w-[72%] sm:max-w-[78%] pointer-events-auto">
						<ProductTag tag={primaryTagForThisCard} nameOnly className="max-w-full" />
					</div>
					{overflowTagCount > 0 && (
						<span
							className="inline-flex shrink-0 items-center justify-center rounded-full px-2 py-1 text-xs font-semibold shadow-sm pointer-events-auto whitespace-nowrap"
							style={{ backgroundColor: primaryColor, color: '#ffffff' }}
						>
							+{overflowTagCount}
						</span>
					)}
				</div>
			)}
			{product.tags && product.tags.length > 0 && (
				<div id="product-card-bgsd-desktop-tags" className="absolute top-0 right-0 w-full p-3 z-50 pointer-events-none hidden lg:block">
					<div className="pointer-events-auto">
						<ProductTagRenderer tags={product.tags} singleRow={true} />
					</div>
				</div>
			)}
			{/* Image area */}
						<div
				id="product-card-bgsd-image-container"
				className="relative w-full flex justify-center items-center aspect-square overflow-hidden"
				style={{ maxWidth: '100%' }}
			>
				<a
					id="product-card-bgsd-link-image"
					href={href}
					className="w-full h-full"
				>
					<SmartImage
						id="product-card-bgsd-img"
						src={imageUrl}
						alt={imageAlt || 'Product image'}
						width={480}
						height={480}
						className="h-full w-full object-contain bg-white rounded-xl"
						loading={priorityImage ? 'eager' : 'lazy'}
						preferResize={true}
					/>
				</a>
				{favoriteEnable && (
					<div className="absolute bottom-2 right-2 z-50">
						<CollectionListItemClient
							productHandle={product.handle}
							refresh={refreshfav}
							className="w-8 h-8 md:w-10 md:h-10 bg-white"
						/>
					</div>
				)}
			</div>

			{/* Content area */}
			<div
				id="product-card-bgsd-content"
				className="flex flex-col gap-6 justify-between flex-1 mt-2"
			>
				<div
					id="product-card-bgsd-text"
					className="flex flex-col gap-[6px]"
				>
					{/* Title */}
					<a
						id="product-card-bgsd-link-title"
						href={href}
					>
						<h3
							id="product-card-bgsd-title"
							className="font-bold line-clamp-3"
							style={{
								color: primaryColor,
								fontSize: '20px',
								lineHeight: '30px',
								wordBreak: 'break-word',
							}}
						>
							{title}
						</h3>
					</a>

					{/* Short description */}
					{shortDescription && (
						<div
							id="product-card-bgsd-short-desc"
							className="line-clamp-5"
							style={{
								color: '#505050',
								fontSize: '16px',
								fontWeight: 400,
								lineHeight: '28px',
								letterSpacing: '0.32px',
								wordBreak: 'break-word',
							}}
							dangerouslySetInnerHTML={{ __html: shortDescription }}
						/>
					)}
				</div>

				{/* CTA Button */}
				<a
					id="product-card-bgsd-link-button"
					href={href}
					className="w-full justify-center flex"
				>
					<button
						id="product-card-bgsd-button"
						className="w-full h-9 px-1 xs:px-4 md:px-2 lg:px-4 font-semibold transition-colors rounded-full whitespace-nowrap text-[11px] sm:text-[12px] md:text-[13px] lg:text-[14px]"
						style={{
							backgroundColor: primaryColor,
							color: buttonTextColor,
                            // borderRadius: borderRadiusShorthand(layoutStyle?.borderRadius || '0'),
							// hover is handled via CSS class below — inline style can't do :hover,
							// so we rely on Tailwind's dynamic-bg utility already registered in other cards
						}}
						onMouseEnter={(e) => {
							(e.currentTarget as HTMLButtonElement).style.backgroundColor = buttonHoverBg;
						}}
						onMouseLeave={(e) => {
							(e.currentTarget as HTMLButtonElement).style.backgroundColor = primaryColor;
						}}
						aria-label={t('product.go_to_product_deatil') ?? 'รายละเอียดสินค้า'}
					>
						<span className="xs:hidden">
							{t('product.go_to_product_detail_short') ?? 'ดูรายละเอียด'}
						</span>
						<span className="hidden xs:inline">
							{t('product.go_to_product_deatil') ?? 'รายละเอียดสินค้า'}
						</span>
					</button>
				</a>
			</div>
		</div>
		<style jsx global>{`
			#product-card-bgsd-desktop-tags #product-tag-renderer-overflow-indicator {
				background-color: #f58620 !important;
				color: #ffffff !important;
			}
		`}</style>
		</>
	);
};

const areEqual = (prev: ProductCardBorderGradientShortDescProps, next: ProductCardBorderGradientShortDescProps) => {
	if (prev === next) return true;
	if (prev.href !== next.href) return false;
	if (prev.level !== next.level) return false;
	if (prev.variantId !== next.variantId) return false;
	if (prev.priorityImage !== next.priorityImage) return false;
	if (prev.shopCurrency !== next.shopCurrency) return false;
	if (prev.refreshfav !== next.refreshfav) return false;
	const pp = prev.product;
	const np = next.product;
	if (pp === np) return true;
	if (!pp || !np) return false;
	if (pp.id !== np.id) return false;
	if (pp.attributes?.displayName !== np.attributes?.displayName) return false;
	if (pp.attributes?.shortDescription !== np.attributes?.shortDescription) return false;
	return true;
};

const Loading = () => (
	<div
		className="flex flex-col w-full h-full animate-pulse"
		style={{
			borderRadius: '10px',
			border: '1px solid transparent',
			background:
				'linear-gradient(white, white) padding-box padding-box, linear-gradient(to top,rgb(255, 153, 90) 0%, rgb(226, 226, 226) 100%) border-box border-box',
			padding: '20px',
		}}
	>
		<div className="w-full aspect-square bg-gray-200 rounded-xl" />
		<div className="flex flex-col gap-3 mt-4">
			<div className="h-6 w-3/4 bg-gray-200 rounded" />
			<div className="h-4 w-full bg-gray-200 rounded" />
			<div className="h-4 w-5/6 bg-gray-200 rounded" />
			<div className="h-9 w-full bg-gray-200 rounded mt-2" />
		</div>
	</div>
);

const ProductCardBorderGradientShortDesc = memo(ProductCardBorderGradientShortDescInner, areEqual);

export default dynamic(() => Promise.resolve(ProductCardBorderGradientShortDesc), {
	ssr: false,
	loading: Loading,
});
